import { useEffect } from "react";

/**
 * Injects JSON-LD structured data for a specific delivery method page.
 * This helps Google and AI assistants (ChatGPT, Perplexity, etc.) understand
 * that Livius Training offers Food Safety / First Aid / etc. via Classroom,
 * Virtual Classroom, and E-Learning.
 */
export default function DeliveryMethodSchema({ deliveryMethod, courses = [], pageTitle, pageDescription, pageUrl }) {
  useEffect(() => {
    const existingScript = document.getElementById("delivery-method-schema");
    if (existingScript) existingScript.remove();

    const deliveryLabels = {
      classroom: "Classroom / In-Person",
      virtual: "Virtual Classroom / Online Live",
      elearning: "E-Learning / Self-Paced Online",
    };

    const deliveryOfferMap = {
      classroom: "OnSitePickup",
      virtual: "OnlineEventAttendanceMode",
      elearning: "OnlineEventAttendanceMode",
    };

    // Build ItemList of courses for this delivery type
    const itemList = courses.slice(0, 20).map((course, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Course",
        name: course.title,
        description: (course.description || "").replace(/<[^>]*>/g, "").slice(0, 300),
        provider: {
          "@type": "Organization",
          name: "Livius Training",
          sameAs: "https://livius-training.co.uk",
        },
        educationalLevel: course.level || "Beginner",
        courseMode: deliveryMethod === "elearning" ? "online" : deliveryMethod === "virtual" ? "online" : "blended",
        offers: {
          "@type": "Offer",
          price: course.price,
          priceCurrency: "GBP",
          availability: "https://schema.org/InStock",
          url: `https://livius-training.co.uk/courses/${course.slug || course.id}`,
        },
        hasCourseInstance: {
          "@type": "CourseInstance",
          courseMode: deliveryMethod === "elearning" ? "online" : deliveryMethod === "virtual" ? "online" : "onsite",
          courseWorkload: course.duration,
          eventAttendanceMode: `https://schema.org/${deliveryOfferMap[deliveryMethod]}`,
        },
      },
    }));

    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ItemList",
          name: pageTitle,
          description: pageDescription,
          url: pageUrl || window.location.href,
          itemListElement: itemList,
        },
        {
          "@type": "Service",
          serviceType: `${deliveryLabels[deliveryMethod]} Training`,
          name: pageTitle,
          description: pageDescription,
          provider: {
            "@type": "Organization",
            name: "Livius Training",
            url: "https://livius-training.co.uk",
            telephone: "+441423396780",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Rabbit Hill Business Park",
              addressLocality: "Boroughbridge",
              addressRegion: "North Yorkshire",
              postalCode: "HG5 0FF",
              addressCountry: "GB",
            },
          },
          areaServed: deliveryMethod === "classroom"
            ? { "@type": "State", name: "Yorkshire" }
            : [
                { "@type": "Country", name: "United Kingdom" },
                { "@type": "Place", name: "Worldwide" },
              ],
          ...(deliveryMethod !== "classroom" ? { availableLanguage: "en" } : {}),
          availableChannel: {
            "@type": "ServiceChannel",
            serviceUrl: pageUrl || window.location.href,
            serviceType: deliveryLabels[deliveryMethod],
          },
        },
      ],
    };

    const script = document.createElement("script");
    script.id = "delivery-method-schema";
    script.type = "application/ld+json";
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById("delivery-method-schema");
      if (s) s.remove();
    };
  }, [deliveryMethod, courses, pageTitle, pageDescription, pageUrl]);

  return null;
}